import { HttpConfig } from "./sub-types/http-config";
import { HttpResponseHeaders, HttpType } from "./sub-types/http-subtypes";
import { Utils } from "../utils/utils";
import { HttpResponse } from "./sub-types/http-response";
import { HttpHeaders } from "./sub-types/http-headers";
import { isNullOrUndefined } from "util";
import set = Reflect.set;

export class Http {
  execute(config: HttpConfig, type: HttpType): Promise<any> {
    const promise: Promise<any> = new Promise<any>((resolve, reject) => {
      const request = new XMLHttpRequest();

      request.onreadystatechange = () => {
        if (
          request.readyState === 4 &&
          request.status >= 200 &&
          request.status < 300
        ) {
          resolve(this.prepareResponse(request));
        } else if (
          request.readyState === 4 &&
          request.status >= 300 &&
          request.status <= 599
        ) {
          reject(this.prepareResponse(request));
        }
      };

      request.onerror = () => {
        reject(this.prepareResponse(request));
      };

      request.withCredentials = Utils.isNullOrUndefined(config.withCredentials) ? true : config.withCredentials as boolean;

      const payload = this.prepareDataToSend(config.data, type);

      if (type === "GET" && Utils.stringNotEmpty(payload)) {
        request.open(type, `${config.url}${payload}`, true);
      } else {
        request.open(type, config.url, true);
      }

      if (type !== "GET") {
        let setDefaultContentType = true;

        if (!Utils.isNullOrUndefined(config.headers)) {
          const parsedHeaders = config.headers as HttpHeaders;

          if (!Utils.isNullOrUndefined(parsedHeaders["Content-Type"])) {
            setDefaultContentType = false;
          }
        }

        if (setDefaultContentType) {
          request.setRequestHeader(
            "Content-Type",
            "application/json;charset=UTF-8"
          );
        }

        if (!Utils.isNullOrUndefined(config.headers)) {
          this.applyAllRequestHeaders(request, config.headers as HttpHeaders);
        }

        request.send(payload);
      } else {
        if (!Utils.isNullOrUndefined(config.headers)) {
          this.applyAllRequestHeaders(request, config.headers as HttpHeaders);
        }

        request.send();
      }
    });

    return promise as Promise<any>;
  }

  get(config: HttpConfig): Promise<any> {
    return this.execute(config, "GET");
  }

  post(config: HttpConfig): Promise<any> {
    return this.execute(config, "POST");
  }

  put(config: HttpConfig): Promise<any> {
    return this.execute(config, "PUT");
  }

  delete(config: HttpConfig): Promise<any> {
    return this.execute(config, "DELETE");
  }

  prepareDataToSend(data: any, type: HttpType): string | undefined {
    let output;

    if (type !== "GET") {
      if (data instanceof URLSearchParams) {
        return data.toString();
      }

      try {
        output = JSON.stringify(data);
      } catch (e) {
        output = data.toString();
      }

      return output;
    } else {
      if (Utils.isObject(data)) {
        output = "?";

        const temporalArray = Object.keys(data).map(key => {
          let value;

          if (Utils.isObject(data[key]) || Utils.isArray(data[key])) {
            try {
              value = encodeURIComponent(JSON.stringify(data[key]));
            } catch (e) {
              value = encodeURIComponent(data[key]);
            }
          } else {
            value = encodeURIComponent(data[key]);
          }

          return `${key}=${value}`;
        });

        output += temporalArray.join("&");

        return output;
      }
    }
  }

  applyAllRequestHeaders(request: XMLHttpRequest, headers: HttpHeaders) {
    Object.keys(headers).forEach(headerKey => {
      request.setRequestHeader(headerKey, headers[headerKey]);
    });
  }

  prepareResponse(request: XMLHttpRequest): HttpResponse {
    let resultantData = this.tryParsingData(request.responseText);

    const responseHeaders = this.parseResponseHeaders(request),
      responseObject = {
        status: request.status,
        requestState: request.readyState,
        data: resultantData,
        responseHeaders
      } as HttpResponse;
    return responseObject;
  }

  parseResponseHeaders(request: XMLHttpRequest): HttpResponseHeaders {
    const output = {} as HttpResponseHeaders;
    if (!request.HEADERS_RECEIVED) {
      console.warn(
        "Request headers are not yet present, returning empty object"
      );
      return output;
    }

    const headers = request.getAllResponseHeaders();

    if (Utils.stringNotEmpty(headers)) {
      const headersArray = headers.trim().split(/[\r\n]+/);

      headersArray.forEach(headerPair => {
        const parts = headerPair.split(": "),
          headerKey = parts.shift(),
          headerValue = parts.join(": ");

        if (Utils.stringNotEmpty(headerKey)) {
          output[headerKey as string] = headerValue;
        }
      });

      return output;
    }

    return output;
  }

  tryParsingData(requestResponseText: string): any {
    if (!Utils.stringNotEmpty(requestResponseText)) {
      return undefined;
    }

    switch (requestResponseText) {
      case "true":
        return true;
      case "false":
        return false;
      case "undefined":
        return undefined;
      case "null":
        return null;
      default:
        const parsedNumber = Number(requestResponseText);

        if (!isNaN(parsedNumber)) {
          return parsedNumber;
        }

        try {
          const parsedObjectOrArray = JSON.parse(requestResponseText);

          return parsedObjectOrArray;
        } catch(e) {
          return requestResponseText;
        }
    }
  }
}

export const httpInstance = new Http();
